import { Toaster, toast } from '@CommonComponents/toast'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { client } from '../utils'

const STATUS_META = {
  verified: {
    label: 'Verified',
    badgeClass: 'label-success',
    iconClass: 'fa-regular fa-check',
  },
  failed: {
    label: 'Failed',
    badgeClass: 'label-danger',
    iconClass: 'fa-regular fa-times',
  },
  stale: {
    label: 'Stale',
    badgeClass: 'label-warning',
    iconClass: 'fa-regular fa-triangle-exclamation',
  },
  syncing: {
    label: 'Syncing',
    badgeClass: 'label-info',
    iconClass: 'fa-regular fa-spinner-third fa-spin',
  },
  pending: {
    label: 'Pending',
    badgeClass: 'label-default',
    iconClass: 'fa-regular fa-clock',
  },
  never_synced: {
    label: 'Never Synced',
    badgeClass: 'label-default',
    iconClass: 'fa-regular fa-minus',
  },
}

const toTitle = txt => txt.charAt(0).toUpperCase() + txt.slice(1)

const formatItems = item => {
  if (item.status === 'never_synced') {
    return '-'
  }

  let txt = `${item.confirmed_count}/${item.expected_count}`
  if (item.failed_count > 0) {
    txt += ` (${item.failed_count} failed)`
  }
  if (item.extra_count > 0) {
    txt += ` (${item.extra_count} extra)`
  }
  return txt
}

const formatDate = value => {
  if (!value) {
    return 'Never'
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return 'Never'
  }
  return parsed.toLocaleString()
}

const App = ({ coursenum }) => {
  const [statusData, setStatusData] = useState([])
  const [aiSyncStatuses, setAiSyncStatuses] = useState([])
  const [chatbotEnabled, setChatbotEnabled] = useState(false)
  const [file, setFile] = useState()
  const [uploadedFile, setUploadedFile] = useState()
  const [error, setError] = useState()
  const [contentType, setContentType] = useState('materials')
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [loadingAiSyncStatus, setLoadingAiSyncStatus] = useState(true)
  const [retryBusy, setRetryBusy] = useState(false)
  const [expandedErrors, setExpandedErrors] = useState({})
  const pollTimerRef = useRef(null)

  const fetchStatusData = useCallback(async () => {
    try {
      const response = await client.get(`/instructor/admin_setup.get_chatbot_status/${coursenum}/`)
      setStatusData(response.data.statuses)
    } catch (err) {
      toast.error(`There was an error while fetching the chatbot status. ${err}`)
    } finally {
      setLoadingStatus(false)
    }
  }, [coursenum])

  const fetchAiSyncStatus = useCallback(async () => {
    try {
      const response = await client.get(`/instructor/admin_setup.get_ai_sync_status/${coursenum}/`)
      setAiSyncStatuses(response.data.statuses || [])
      setChatbotEnabled(Boolean(response.data.chatbot_enabled))
    } catch (err) {
      toast.error(`There was an error while fetching AI sync status. ${err}`)
    } finally {
      setLoadingAiSyncStatus(false)
    }
  }, [coursenum])

  useEffect(() => {
    fetchStatusData()
    fetchAiSyncStatus()
  }, [fetchAiSyncStatus, fetchStatusData])

  useEffect(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }

    if (aiSyncStatuses.some(item => item.status === 'syncing' || item.status === 'pending')) {
      pollTimerRef.current = setTimeout(() => {
        fetchAiSyncStatus()
      }, 10000)
    }

    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [aiSyncStatuses, fetchAiSyncStatus])

  const handleSendChatbotData = async () => {
    const maxRetries = 3
    let retryCount = 0

    const sendData = async () => {
      try {
        await client.post(`/instructor/admin_setup.send_textbook_data_to_chatbot/${coursenum}/`)
        window.location.reload()
      } catch (err) {
        if (retryCount < maxRetries) {
          retryCount++
          await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** (retryCount - 1))) // Exponential backoff
          return sendData()
        }
        toast.error(`There was an error while sending the chatbot data. ${err}`)
        throw err // Re-throw to be caught by error boundary if present
      }
    }

    await sendData()
  }

  const handleMissingPagesCheck = async () => {
    try {
      const response = await client.post(`/instructor/admin_setup.check_missing_pages/${coursenum}/`)
      const data = response.data
      toast.info(`Missing Pages Check \nTotal pages: ${data.total_pages}
Missing pages: ${data.missing_pages}
Sample missing IDs: ${data.missing_page_ids.join(', ')}`
      )
    } catch (err) {
      toast.error(`Error checking missing pages: ${err}`)
    }
  }

  const handleChange = (event) => {
    const selectedFile = event.target.files[0]
    if (!selectedFile) {
      setFile(null)
      return
    }
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase()
    if (fileExtension !== 'pdf' && fileExtension !== 'txt') {
      toast.error('Invalid file type. Only PDF and TXT files are accepted.')
      setFile(null)
      return
    }
    setFile(selectedFile)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!file) {
      toast.error('Please select a file before uploading.')
      return
    }
    const url = `/instructor/admin_setup.upload_chatbot_context_file/${coursenum}/`
    const formData = new FormData()
    formData.append('file', file)
    formData.append('fileName', file.name)
    formData.append('coursenum', coursenum)
    formData.append('fileType', contentType)
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }
    await client.post(url, formData, config)
      .then((response) => {
        setUploadedFile(response.data.file)
      })
      .catch((error) => {
        toast.error('Error uploading file. Please try again.')
        setError(error)
      })
  }

  const handleDelete = async (id, type) => {
    const url = `/instructor/admin_setup.delete_chatbot_context_file/${coursenum}/`
    const payload = { document_id: id, file_type: type }

    try {
      await client.post(url, payload)
      toast.success("File deleted successfully.")
      const updatedStatusData = statusData.filter(item => item.type !== type)
      setStatusData(updatedStatusData)
    } catch (error) {
      console.error("Error deleting file: ", error)
      setError(error)
      toast.error(`There was an error while deleting the file. ${error}`)
    }
  }

  const handleRetryAiSync = async payload => {
    setRetryBusy(true)
    try {
      const response = await client.post(
        `/instructor/admin_setup.retry_ai_sync/${coursenum}/`,
        payload
      )
      const queuedTypes = response.data.queued_types || []
      const skippedTypes = response.data.skipped_types || []

      if (queuedTypes.length > 0) {
        toast.success(`Queued AI sync for: ${queuedTypes.join(', ')}`)
      } else {
        toast.info('No AI sync tasks were queued.')
      }

      if (skippedTypes.length > 0) {
        const skippedSummary = skippedTypes.map(
          item => `${item.content_type} (${item.reason})`
        ).join(', ')
        toast.info(`Skipped: ${skippedSummary}`)
      }

      await fetchAiSyncStatus()
    } catch (err) {
      toast.error(`There was an error while queueing AI sync. ${err}`)
    } finally {
      setRetryBusy(false)
    }
  }

  const handleRetrySingle = async contentTypeToRetry => {
    await handleRetryAiSync({ content_types: [contentTypeToRetry] })
  }

  const handleForceFullResync = async () => {
    const shouldContinue = window.confirm(
      'This will re-sync all content types, including verified ones. Continue?'
    )
    if (!shouldContinue) {
      return
    }
    await handleRetryAiSync({ force: true })
  }

  const toggleErrorExpanded = contentTypeToToggle => {
    setExpandedErrors(prev => ({
      ...prev,
      [contentTypeToToggle]: !prev[contentTypeToToggle],
    }))
  }

  const handleFileTypeChange = (event) => {
    setContentType(event.target.value)
  }

  return (
    <React.Fragment>
      <Toaster />
      <h3>Chatbot Services</h3>
      <div
        style={{
          marginBottom: '20px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <Button
          className="btn btn-myed btn-default btn-default-myed btn-sm-myed"
          onClick={handleSendChatbotData}
        >
          Send Chatbot Data
        </Button>
        <Button
          className="btn btn-myed btn-default btn-default-myed btn-sm-myed"
          onClick={handleMissingPagesCheck}
        >
          Missing Pages Check
        </Button>
      </div>

      {chatbotEnabled && (
        <div style={{ marginBottom: '20px', marginTop: '10px', maxWidth: '100%' }}>
          <h4>AI Data Sync Status</h4>
          <div
            style={{
              marginBottom: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <Button
              className="btn btn-myed btn-default btn-default-myed btn-xs-myed"
              onClick={fetchAiSyncStatus}
              disabled={loadingAiSyncStatus || retryBusy}
            >
              Refresh
            </Button>
            <Button
              className="btn btn-myed btn-default btn-default-myed btn-xs-myed"
              onClick={() => handleRetryAiSync({})}
              disabled={retryBusy}
            >
              Retry All Unverified
            </Button>
            <Button
              className="btn btn-myed btn-default btn-default-myed btn-xs-myed"
              onClick={handleForceFullResync}
              disabled={retryBusy}
            >
              Force Full Re-sync
            </Button>
          </div>

          {loadingAiSyncStatus ? (
            <div style={{ textAlign: 'center', margin: '20px' }}>Loading AI sync status...</div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '12px',
              }}
            >
              {aiSyncStatuses.map(item => {
                const meta = STATUS_META[item.status] || STATUS_META.pending
                return (
                  <div
                    key={item.content_type}
                    style={{
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      background: '#fff',
                      padding: '12px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '8px',
                        marginBottom: '10px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '18px', fontWeight: 600, lineHeight: 1.2, marginBottom: '8px' }}>
                          {toTitle(item.content_type)}
                        </div>
                        <span className={`label ${meta.badgeClass}`}>
                          <i className={meta.iconClass} style={{ marginRight: '6px' }} />
                          {meta.label}
                        </span>
                      </div>

                      {item.can_retry ? (
                        <Button
                          className="btn btn-myed btn-default btn-default-myed btn-xs-myed"
                          onClick={() => handleRetrySingle(item.content_type)}
                          disabled={retryBusy}
                        >
                          Retry
                        </Button>
                      ) : null}
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '8px',
                        marginBottom: item.status === 'failed' && item.error ? '8px' : 0,
                      }}
                    >
                      <div style={{ background: '#f8f8f8', borderRadius: '6px', padding: '8px' }}>
                        <div style={{ color: '#666', fontSize: '12px', marginBottom: '2px' }}>Items</div>
                        <div style={{ fontWeight: 600 }}>{formatItems(item)}</div>
                      </div>
                      <div style={{ background: '#f8f8f8', borderRadius: '6px', padding: '8px' }}>
                        <div style={{ color: '#666', fontSize: '12px', marginBottom: '2px' }}>Last Synced</div>
                        <div style={{ fontWeight: 600 }}>{formatDate(item.last_synced)}</div>
                      </div>
                    </div>

                    {item.status === 'failed' && item.error && (
                      <div className="text-danger" style={{ maxWidth: '100%' }}>
                        <div
                          style={{
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            lineHeight: '1.35',
                          }}
                        >
                          {item.error}
                        </div>
                        {item.full_error && item.full_error.length > item.error.length && (
                          <React.Fragment>
                            <Button
                              variant="link"
                              className="btn btn-link btn-xs p-0"
                              style={{ marginTop: '4px' }}
                              onClick={() => toggleErrorExpanded(item.content_type)}
                            >
                              {expandedErrors[item.content_type] ? 'Show less' : 'Show more'}
                            </Button>
                            {expandedErrors[item.content_type] && (
                              <pre
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                  marginTop: '6px',
                                  maxHeight: '180px',
                                  overflowY: 'auto',
                                  padding: '8px',
                                  background: '#fafafa',
                                  border: '1px solid #eee',
                                  borderRadius: '4px',
                                }}
                              >
                                {item.full_error}
                              </pre>
                            )}
                          </React.Fragment>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      <div className="chatbot-file-upload">
        <h3>Chatbot File Upload</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="fileType">Select file type:</label>
            <select
              id="fileType"
              name="fileType"
              className="form-control"
              value={contentType}
              onChange={handleFileTypeChange}
            >
              <option value="materials">Materials</option>
              <option value="syllabus">Syllabus</option>
              <option value="assignments">Assignments</option>
              <option value="exams">Exams</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="file">Choose a file:</label>
            <input
              type="file"
              id="file"
              accept=".pdf,.txt"
              className="form-control-file"
              onChange={handleChange}
            />
          </div>

          <Button type="submit" variant="secondary" className="btn-myed btn-default-myed btn-sm-myed">
            Upload
          </Button>
        </form>

        {uploadedFile && (
          <div className="mt-3">
            <p className='label label-success'>File uploaded successfully</p>
          </div>
        )}
        {error && (
          <p className="text-danger mt-3">
            Error uploading file: {error.message}
          </p>
        )}
      </div>
      {loadingStatus ? (
        <div style={{textAlign: "center", margin: "20px"}}>Loading Chatbot context status...</div>

      ): <div></div>}
      {statusData.length > 0 && (
        <div>
          <h4>Chatbot Context Status:</h4>
          {statusData.map((item, index) => (
            <div key={index} style={{ border: '1px solid gray', borderRadius: '5px', padding: '10px', marginBottom: '10px' }}>
              <p><strong>Type:</strong> {item.type}</p>
              <p>
                <strong>Status:</strong>
                <span className={`label label-${item.status === 'completed' ? 'success' : item.status === 'processing' ? 'warning' : 'danger'}`}>
                  {item.status}
                </span>
              </p>
              {item.progress && (
                <div className="progress-info">
                  <div className="progress" style={{ height: '20px', marginBottom: '5px' }}>
                    <div
                      className="progress-bar progress-bar-success"
                      style={{ width: `${(item.progress.completed / item.progress.total) * 100}%` }}
                      title={`${item.progress.completed} completed`}
                    />
                    <div
                      className="progress-bar progress-bar-warning"
                      style={{ width: `${(item.progress.processing / item.progress.total) * 100}%` }}
                      title={`${item.progress.processing} processing`}
                    />
                    <div
                      className="progress-bar progress-bar-danger"
                      style={{ width: `${(item.progress.error / item.progress.total) * 100}%` }}
                      title={`${item.progress.error} error`}
                    />
                  </div>
                  <div className="progress-legend" style={{ fontSize: '0.9em', color: '#666' }}>
                    <span style={{ marginRight: '15px' }}>
                      <i className="fa fa-square" style={{ color: '#5cb85c' }}></i> Completed: {item.progress.completed}
                    </span>
                    <span style={{ marginRight: '15px' }}>
                      <i className="fa fa-square" style={{ color: '#f0ad4e' }}></i> Processing: {item.progress.processing}
                    </span>
                    <span>
                      <i className="fa fa-square" style={{ color: '#d9534f' }}></i> Error: {item.progress.error}
                    </span>
                  </div>
                </div>
              )}
              <p><strong>Last Updated:</strong> {new Date(item.lastUpdated).toLocaleString()}</p>
              <Button variant="danger" onClick={() => handleDelete(item.id, item.type)}>Delete</Button>
            </div>
          ))}
        </div>
      )}

    </React.Fragment>
  )
}

export default App
